import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, X, ExternalLink, Table2, Plus, ChevronDown } from "lucide-react";
import { getToken, authFetch } from "@/adminAuth";
import RichEditor from "@/components/RichEditor";
import AiAssistant from "@/components/AiAssistant";
import AdminImageMap from "@/components/AdminImageMap";

const API = `/api`;

const input =
  "w-full bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors";
const area = input + " font-mono text-xs leading-relaxed";

const RELATION_KINDS = [
  { value: "peut_introduire", label: "Peut introduire" },
  { value: "peut_faire_usage_de", label: "Peut faire usage de" },
  { value: "peut_etre_utilise_par", label: "Peut être utilisé par" },
  { value: "demandeur", label: "Peut être introduit par" },
  { value: "defendeur", label: "Peut être introduit contre" },
  { value: "partie", label: "Partie à" },
  { value: "base_legale", label: "Base légale" },
  { value: "est_partie_de", label: "Fait partie de" },
  { value: "est_oppose_a", label: "Est opposé à" },
  { value: "obligation_pour", label: "Obligation pour" },
  { value: "lie_a", label: "Lié à" },
];

const RELATION_INVERSE = {
  peut_introduire: "Peut être introduit par",
  peut_faire_usage_de: "Peut être utilisé par",
  peut_etre_utilise_par: "Peut faire usage de",
  demandeur: "Peut introduire",
  defendeur: "Peut faire l'objet de",
  partie: "A pour partie",
  base_legale: "Base légale de",
  est_partie_de: "Comprend",
  est_oppose_a: "Est opposé à",
  obligation_pour: "Obligation pour",
  lie_a: "Lié à",
};

const kindLabel = (v) => RELATION_KINDS.find((k) => k.value === v)?.label || v;

function TokenInput({ value, onChange, suggestions, placeholder, testid }) {
  const [focus, setFocus] = useState(false);
  const tokens = value.split(",").map((t) => t.trim());
  const current = (tokens[tokens.length - 1] || "").toLowerCase();
  const existing = tokens.slice(0, -1).map((t) => t.toLowerCase());
  const matches = current
    ? suggestions
        .filter((s) => s.toLowerCase().includes(current) && !existing.includes(s.toLowerCase()))
        .slice(0, 8)
    : [];
  const pick = (s) => {
    onChange([...tokens.slice(0, -1), s].filter(Boolean).join(", ") + ", ");
  };
  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        placeholder={placeholder}
        className={input}
        data-testid={testid}
      />
      {focus && matches.length > 0 && (
        <div
          className="absolute z-20 left-0 right-0 top-full mt-1 bg-ink-card border border-gold/25 max-h-56 overflow-y-auto"
          data-testid={`${testid}-suggestions`}
        >
          {matches.map((s) => (
            <button
              key={s}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              className="w-full text-left px-4 py-2 text-xs text-[#A0A5B1] hover:bg-gold/10 hover:text-gold transition-colors"
              data-testid={`${testid}-suggestion`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FichePicker({ fiches, value, onChange, exclude, resetSignal, testid }) {
  const [text, setText] = useState("");
  const [focus, setFocus] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  useEffect(() => { setText(""); }, [resetSignal]);
  const types = [...new Map(fiches.map((f) => [f.type, f.type_label || f.type])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "fr"));
  const q = text.toLowerCase().trim();
  const matches = q
    ? fiches
        .filter((f) => `${f.type}/${f.slug}` !== exclude)
        .filter((f) => !typeFilter || f.type === typeFilter)
        .filter((f) => `${f.title} ${f.slug} ${f.type_label || f.type}`.toLowerCase().includes(q))
        .slice(0, 10)
    : [];
  const pick = (f) => {
    onChange(`${f.type}/${f.slug}`);
    setText(`${f.title} (${f.type_label || f.type})`);
    setFocus(false);
  };
  return (
    <div className="flex flex-col sm:flex-row gap-3 flex-1">
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        className={input + " bg-ink cursor-pointer sm:w-44"}
        data-testid={`${testid}-type-filter`}
      >
        <option value="" className="bg-ink">Tous types</option>
        {types.map(([k, l]) => (
          <option key={k} value={k} className="bg-ink">{l}</option>
        ))}
      </select>
      <div className="relative flex-1">
        <input
          value={text}
          onChange={(e) => { setText(e.target.value); if (value) onChange(""); }}
          onFocus={() => setFocus(true)}
          onBlur={() => setTimeout(() => setFocus(false), 150)}
          placeholder="Tapez pour rechercher une fiche…"
          className={input}
          data-testid={testid}
        />
        {focus && q && (
          <div
            className="absolute z-20 left-0 right-0 top-full mt-1 bg-ink-card border border-gold/25 max-h-64 overflow-y-auto"
            data-testid={`${testid}-suggestions`}
          >
            {matches.length === 0 ? (
              <p className="px-4 py-2 text-xs text-[#626773] italic">Aucune fiche ne correspond.</p>
            ) : (
              matches.map((f) => (
                <button
                  key={`${f.type}/${f.slug}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(f); }}
                  className="w-full text-left px-4 py-2 text-xs text-[#A0A5B1] hover:bg-gold/10 hover:text-gold transition-colors"
                  data-testid={`${testid}-suggestion`}
                >
                  {f.title} <span className="text-[#626773]">({f.type_label || f.type})</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminSection({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left group"
      >
        <span className="label-accent group-hover:text-gold transition-colors">{title}</span>
        <ChevronDown className={`w-4 h-4 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {/* Repli simple (display none) : PAS d'overflow hidden ici, sinon la barre d'outils
          sticky de l'editeur (section Contenu) cesse de fonctionner. Le contenu reste monte. */}
      <div className={open ? "px-5 pb-6 pt-1 space-y-8" : "hidden"}>{children}</div>
    </div>
  );
}

export default function AdminFicheEdit() {
  const { type, slug } = useParams();
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [team, setTeam] = useState([]);
  const [tags, setTags] = useState("");
  const [cats, setCats] = useState("");
  const [taxonomy, setTaxonomy] = useState(null);
  const [vocab, setVocab] = useState({ tags: [], categories: [], types: [] });
  const [allFiches, setAllFiches] = useState([]);
  const [relKind, setRelKind] = useState("lie_a");
  const [relTarget, setRelTarget] = useState("");
  const [pickerReset, setPickerReset] = useState(0);
  const [mode, setMode] = useState("edit");
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [tabGenerating, setTabGenerating] = useState(false);
  const [tabError, setTabError] = useState("");

  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    authFetch(`${API}/admin/knowledge/${type}/${slug}`)
      .then((r) => r.json())
      .then((d) => {
        setFiche(d);
        setTags((d.tags || []).join(", "));
        setCats((d.categories || []).join(", "));
      })
      .catch(() => {});
    fetch(`${API}/team`).then((r) => r.json()).then(setTeam).catch(() => {});
    fetch(`${API}/knowledge/taxonomy`).then((r) => r.json()).then(setTaxonomy).catch(() => {});
    authFetch(`${API}/admin/knowledge`)
      .then((r) => r.json())
      .then((d) => setAllFiches(Array.isArray(d) ? d : []))
      .catch(() => {});
    authFetch(`${API}/admin/vocab`)
      .then((r) => r.json())
      .then((d) => setVocab({ tags: d.tags || [], categories: d.categories || [], types: d.types || [] }))
      .catch(() => {});
  }, [type, slug]);

  if (!fiche) return <div className="pt-40 min-h-screen px-6 text-[#626773] font-mono text-sm">Chargement…</div>;

  const set = (k) => (e) => setFiche({ ...fiche, [k]: e.target.value });

  const toggleList = (field, key) =>
    setFiche((f) => {
      const list = f[field] || [];
      return {
        ...f,
        [field]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key],
      };
    });

  const addRelation = () => {
    if (!relTarget) return;
    const [t, s] = relTarget.split("/");
    const target = allFiches.find((f) => f.type === t && f.slug === s);
    if (!target) return;
    const list = fiche.relations || [];
    if (list.some((r) => r.kind === relKind && r.type === t && r.slug === s)) return;
    setFiche({
      ...fiche,
      relations: [...list, { kind: relKind, type: t, slug: s, title: target.title }],
    });
    setRelTarget("");
    setPickerReset((n) => n + 1);
  };

  const removeRelation = (idx) =>
    setFiche({ ...fiche, relations: fiche.relations.filter((_, i) => i !== idx) });

  const generatePlanche = async () => {
    if (generating) return;
    setGenerating(true);
    setGenError("");
    try {
      const resp = await authFetch(`${API}/admin/knowledge/${type}/${slug}/generate-planche`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.image) {
        setFiche((f) => ({ ...f, image: data.image }));
      } else {
        setGenError(typeof data.detail === "string" ? data.detail : "La génération a échoué — réessayez.");
      }
    } catch {
      setGenError("La génération a échoué — réessayez.");
    }
    setGenerating(false);
  };

  const patchTableau = (id, fn) =>
    setFiche((f) => ({
      ...f,
      tableaux: (f.tableaux || []).map((tb) => (tb.id === id ? fn(tb) : tb)),
    }));

  const updateTableau = (id, patch) => patchTableau(id, (tb) => ({ ...tb, ...patch }));

  const updateCell = (id, r, c, value) =>
    patchTableau(id, (tb) => {
      if (r === -1)
        return { ...tb, columns: tb.columns.map((col, i) => (i === c ? value : col)) };
      return {
        ...tb,
        rows: tb.rows.map((row, i) =>
          i === r ? row.map((cell, j) => (j === c ? value : cell)) : row
        ),
      };
    });

  const addRow = (id) =>
    patchTableau(id, (tb) => ({ ...tb, rows: [...tb.rows, Array(tb.columns.length).fill("")] }));

  const removeRow = (id, r) =>
    patchTableau(id, (tb) => ({ ...tb, rows: tb.rows.filter((_, i) => i !== r) }));

  const addCol = (id) =>
    patchTableau(id, (tb) => ({
      ...tb,
      columns: [...tb.columns, `Colonne ${tb.columns.length + 1}`],
      rows: tb.rows.map((row) => [...row, ""]),
    }));

  const removeCol = (id, c) =>
    patchTableau(id, (tb) =>
      tb.columns.length <= 2
        ? tb
        : {
            ...tb,
            columns: tb.columns.filter((_, i) => i !== c),
            rows: tb.rows.map((row) => row.filter((_, i) => i !== c)),
          }
    );

  const removeTableau = (id) =>
    setFiche((f) => ({ ...f, tableaux: (f.tableaux || []).filter((tb) => tb.id !== id) }));

  const addManualTableau = () =>
    setFiche((f) => ({
      ...f,
      tableaux: [
        ...(f.tableaux || []),
        {
          id: Math.random().toString(36).slice(2, 10),
          form: "table",
          title: "",
          columns: ["Colonne 1", "Colonne 2"],
          rows: [["", ""]],
        },
      ],
    }));

  const generateTableau = async () => {
    if (tabGenerating) return;
    setTabGenerating(true);
    setTabError("");
    try {
      const resp = await authFetch(`${API}/admin/knowledge/${type}/${slug}/generate-tableau`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.tableau) {
        setFiche((f) => ({ ...f, tableaux: [...(f.tableaux || []), data.tableau] }));
      } else {
        setTabError(typeof data.detail === "string" ? data.detail : "La génération a échoué — réessayez.");
      }
    } catch {
      setTabError("La génération a échoué — réessayez.");
    }
    setTabGenerating(false);
  };

  const updateField = (i, patch) =>
    setFiche((f) => ({
      ...f,
      fields: (f.fields || []).map((x, j) => (j === i ? { ...x, ...patch } : x)),
    }));

  const removeField = (i) =>
    setFiche((f) => ({ ...f, fields: (f.fields || []).filter((_, j) => j !== i) }));

  const addField = () =>
    setFiche((f) => ({
      ...f,
      fields: [...(f.fields || []), { section: "Informations", label: "", html: "", kind: "row" }],
    }));

  const save = async () => {
    const body = {
      title: fiche.title,
      slug: fiche.slug,
      seo_title: fiche.seo_title || "",
      type: fiche.type,
      excerpt: fiche.excerpt,
      content_html: fiche.content_html,
      image: fiche.image || null,
      author: fiche.author || null,
      show_planche: !!fiche.show_planche,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      categories: cats.split(",").map((c) => c.trim()).filter(Boolean),
      related_slugs: fiche.related_slugs || [],
      poles: fiche.poles || [],
      themes: fiche.themes || [],
      relations: fiche.relations || [],
      tableaux: fiche.tableaux || [],
      fields: fiche.fields || [],
      imagemap: fiche.imagemap || undefined,
    };
    const resp = await authFetch(`${API}/admin/knowledge/${type}/${slug}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (resp.ok) {
      setSaveError(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (fiche.type !== type || fiche.slug !== slug) {
        navigate(`/admin/fiches/${fiche.type}/${fiche.slug}`, { replace: true });
      }
    } else {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 4000);
    }
  };

  const toggleStatus = async () => {
    const next = fiche.status === "publie" ? "archive" : "publie";
    const resp = await authFetch(`${API}/admin/knowledge/${type}/${slug}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    if (resp.ok) setFiche({ ...fiche, status: next });
  };

  const removeRelated = (idx) =>
    setFiche({ ...fiche, related_slugs: fiche.related_slugs.filter((_, i) => i !== idx) });

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-fiche-edit">
      <div className="max-w-4xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/admin"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Toutes les fiches
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/admin?nouvelle=1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-new-fiche-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Nouvelle fiche
            </Link>
            <Link
              to={`/ressources/${type}/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-public"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#626773] hover:text-gold transition-colors"
            >
              Voir la fiche <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={save}
              data-testid="admin-save-button-top"
              className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.2em] font-medium px-4 py-2.5 hover:bg-gold-light transition-colors"
            >
              <Save className="w-3.5 h-3.5" /> Enregistrer
            </button>
            <button
              onClick={toggleStatus}
              data-testid="admin-status-toggle"
              className={`text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${
                fiche.status === "publie"
                  ? "border-red-400/40 text-red-400 hover:bg-red-400/10"
                  : "border-gold/40 text-gold hover:bg-gold/10"
              }`}
            >
              {fiche.status === "publie" ? "Dépublier" : "Publier"}
            </button>
          </div>
        </div>
        {saved && (
          <p className="mt-4 text-gold text-sm text-right" data-testid="admin-saved-confirmation-top">Modifications enregistrées.</p>
        )}
        {saveError && (
          <p className="mt-4 text-red-400 text-sm text-right" data-testid="admin-save-error-top">Échec de l'enregistrement — réessayez.</p>
        )}

        <div className="mt-10 space-y-8">
          <AdminSection title="Fiche, type & planche" defaultOpen testid="admin-sec-fiche">
          <div>
            <label className="label-accent block mb-3">Titre</label>
            <input value={fiche.title} onChange={set("title")} className={input} data-testid="admin-title-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="label-accent block mb-3">Slug (adresse de la page)</label>
              <input value={fiche.slug || ""} onChange={set("slug")} className={`${input} font-mono text-sm`} data-testid="admin-slug-input" />
            </div>
            <div>
              <label className="label-accent block mb-3">Titre SEO (optionnel — sinon le titre de la fiche)</label>
              <input value={fiche.seo_title || ""} onChange={set("seo_title")} className={input} data-testid="admin-seo-title-input" placeholder="Titre affiché dans Google" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="label-accent block mb-3">Type de fiche</label>
              <select
                value={fiche.type}
                onChange={(e) => setFiche({ ...fiche, type: e.target.value })}
                className={input + " bg-ink cursor-pointer"}
                data-testid="admin-type-select"
              >
                {(vocab.types || []).map((t) => (
                  <option key={t.key} value={t.key} className="bg-ink">{t.label}</option>
                ))}
              </select>
              {fiche.type !== type && (
                <p className="mt-2 text-xs text-gold/70" data-testid="admin-type-changed-hint">
                  Le changement de type sera appliqué à l'enregistrement.
                </p>
              )}
            </div>
            <div>
              <label className="label-accent block mb-3">Attribution (auteur)</label>
              <select
                value={fiche.author ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setFiche({ ...fiche, author: v === "" ? null : /^\d+$/.test(v) ? Number(v) : v });
                }}
                className={input + " bg-ink cursor-pointer"}
                data-testid="admin-author-select"
              >
                <option value="" className="bg-ink">Non attribuée</option>
                {typeof fiche.author === "string" && fiche.author && (
                  <option value={fiche.author} className="bg-ink">{fiche.author}</option>
                )}
                {team.map((m) => (
                  <option key={m.slug} value={m.authorId} className="bg-ink">{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-accent block mb-3">Image (URL de la planche)</label>
              <input value={fiche.image || ""} onChange={set("image")} className={input} data-testid="admin-image-input" />
              {fiche.image && (
                <img
                  src={fiche.image}
                  alt="Aperçu de la planche"
                  className="mt-4 max-w-[220px] border border-white/10 bg-[#F5F0E6]"
                  data-testid="admin-image-preview"
                />
              )}
              <div className="mt-4">
                <button
                  onClick={generatePlanche}
                  disabled={generating}
                  data-testid="admin-generate-planche"
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 transition-colors disabled:opacity-50"
                >
                  {generating ? "Génération en cours… (30-60 s)" : "Générer une planche vintage (IA)"}
                </button>
                {genError && (
                  <p className="mt-2 text-xs text-red-400" data-testid="admin-generate-error">{genError}</p>
                )}
                <p className="mt-2 text-xs text-[#626773] leading-relaxed">
                  Gravure vintage cohérente avec la charte, créée à partir du titre et de l'extrait
                  (clé universelle — quelques centimes par génération). L'image est assignée
                  immédiatement ; régénérez si elle ne convient pas, puis Enregistrer la fiche.
                </p>
              </div>
              <label className="mt-3 flex items-center gap-3 cursor-pointer group" data-testid="admin-show-planche-label">
                <input
                  type="checkbox"
                  checked={!!fiche.show_planche}
                  onChange={(e) => setFiche({ ...fiche, show_planche: e.target.checked })}
                  className="accent-[#C5A059] w-4 h-4"
                  data-testid="admin-show-planche-checkbox"
                />
                <span className="text-xs text-[#A0A5B1] group-hover:text-gold transition-colors">
                  Afficher la planche en grand sur la page publique (sinon : visuel de bannière, masqué)
                </span>
              </label>
            </div>
          </div>
          </AdminSection>

          <AdminSection title="Classification (étiquettes, catégories, pôles, thèmes)" testid="admin-sec-classification">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="label-accent block mb-3">Étiquettes (séparées par des virgules)</label>
              <TokenInput
                value={tags}
                onChange={setTags}
                suggestions={vocab.tags}
                placeholder="Ex. : Entreprise, contrat — les valeurs existantes se proposent à la frappe"
                testid="admin-tags-input"
              />
            </div>
            <div>
              <label className="label-accent block mb-3">Catégories (séparées par des virgules)</label>
              <TokenInput
                value={cats}
                onChange={setCats}
                suggestions={vocab.categories}
                placeholder="Ex. : Créer et structurer"
                testid="admin-cats-input"
              />
            </div>
          </div>

          {taxonomy && (
            <div className="border border-white/10 p-6" data-testid="admin-taxonomy-block">
              <label className="label-accent block mb-3">Pôles d'expertise</label>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {taxonomy.poles.map((p) => (
                  <label key={p.key} className="flex items-center gap-2.5 cursor-pointer group" data-testid={`admin-pole-${p.key}`}>
                    <input
                      type="checkbox"
                      checked={(fiche.poles || []).includes(p.key)}
                      onChange={() => toggleList("poles", p.key)}
                      className="accent-[#C5A059] w-4 h-4"
                    />
                    <span className="text-xs text-[#A0A5B1] group-hover:text-gold transition-colors">{p.label}</span>
                  </label>
                ))}
              </div>
              <label className="label-accent block mt-6 mb-3">Thèmes</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                {taxonomy.themes.map((t) => (
                  <label key={t.key} className="flex items-center gap-2.5 cursor-pointer group" data-testid={`admin-theme-${t.key}`}>
                    <input
                      type="checkbox"
                      checked={(fiche.themes || []).includes(t.key)}
                      onChange={() => toggleList("themes", t.key)}
                      className="accent-[#C5A059] w-4 h-4"
                    />
                    <span className="text-xs text-[#A0A5B1] group-hover:text-gold transition-colors">{t.label}</span>
                  </label>
                ))}
              </div>
              <p className="mt-4 text-xs text-[#626773] leading-relaxed">
                Rattachement déduit automatiquement des étiquettes et catégories WordPress.
                Toute modification ci-dessus verrouille la fiche : elle ne sera plus réattribuée automatiquement.
              </p>
            </div>
          )}
          </AdminSection>

          <AdminSection title="Relations & liens" testid="admin-sec-relations">
          <div className="border border-white/10 p-6" data-testid="admin-relations-block">
            <label className="label-accent block mb-3">Relations typées (acteurs, base légale…)</label>
            {(fiche.relations || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-relations-list">
                {fiche.relations.map((r, idx) => (
                  <span key={idx} className="inline-flex items-center gap-2 text-xs text-gold/80 border border-gold/25 px-3 py-1.5">
                    <span className="text-[#626773]">{kindLabel(r.kind)} :</span>
                    <a
                      href={`/admin/fiches/${r.type}/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-gold hover:underline underline-offset-2 transition-colors"
                      data-testid={`admin-relation-view-${idx}`}
                    >
                      {r.title || r.slug}
                    </a>
                    <button onClick={() => removeRelation(idx)} data-testid={`admin-relation-remove-${idx}`} aria-label="Retirer" className="hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={relKind}
                onChange={(e) => setRelKind(e.target.value)}
                className={input + " bg-ink cursor-pointer sm:w-56"}
                data-testid="admin-relation-kind"
              >
                {RELATION_KINDS.map((k) => (
                  <option key={k.value} value={k.value} className="bg-ink">{k.label}</option>
                ))}
              </select>
              <FichePicker
                fiches={allFiches}
                value={relTarget}
                onChange={setRelTarget}
                exclude={`${type}/${slug}`}
                resetSignal={pickerReset}
                testid="admin-relation-target"
              />
              <button
                onClick={addRelation}
                data-testid="admin-relation-add"
                className="border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 transition-colors shrink-0"
              >
                Ajouter
              </button>
            </div>
            <p className="mt-3 text-xs text-[#626773] leading-relaxed">
              Ex. : sur la fiche de l'acteur « Curateur », ajouter « Peut introduire » → les procédures concernées.
              Les relations s'affichent sur la page publique de la fiche.
            </p>

            {(fiche.incoming_relations || []).filter(
              (inc) =>
                !(fiche.relations || []).some(
                  (r) =>
                    r.type === inc.type && r.slug === inc.slug &&
                    (RELATION_KINDS.find((k) => k.value === r.kind)?.label || r.kind) ===
                      (RELATION_INVERSE[inc.kind] || inc.kind)
                )
            ).length > 0 && (
              <div className="mt-5" data-testid="admin-incoming-relations">
                <label className="label-accent block mb-3">Liens entrants (définis sur d'autres fiches)</label>
                <div className="flex flex-wrap gap-2">
                  {fiche.incoming_relations
                    .filter(
                      (inc) =>
                        !(fiche.relations || []).some(
                          (r) =>
                            r.type === inc.type && r.slug === inc.slug &&
                            (RELATION_KINDS.find((k) => k.value === r.kind)?.label || r.kind) ===
                              (RELATION_INVERSE[inc.kind] || inc.kind)
                        )
                    )
                    .map((r, idx) => (
                    <span key={idx} className="inline-flex items-center gap-2 text-xs text-[#A0A5B1] border border-white/15 px-3 py-1.5">
                      <span className="text-[#626773]">{RELATION_INVERSE[r.kind] || r.kind} :</span>
                      <a
                        href={`/admin/fiches/${r.type}/${r.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-gold hover:underline underline-offset-2 transition-colors"
                        data-testid={`admin-incoming-view-${idx}`}
                      >
                        {r.title || r.slug}{r.status && r.status !== "publie" ? " (brouillon)" : ""}
                      </a>
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-[#626773]">
                  Ces liens se modifient depuis la fiche d'origine — ils s'affichent automatiquement ici et sur la page publique.
                </p>
              </div>
            )}
          </div>
          </AdminSection>

          <AdminSection title="Tableaux & visuels interactifs" testid="admin-sec-tableaux">
          <div className="border border-white/10 p-6" data-testid="admin-tableaux-block">
            <label className="label-accent block mb-3">Tableaux explicatifs</label>
            <p className="mb-4 text-xs text-[#626773] leading-relaxed">
              Tableaux ou étapes qui expliquent la fiche — visibles côté public derrière un bouton
              (comme les planches). Générez une proposition par IA puis corrigez, ou rédigez à la main.
            </p>

            {(fiche.tableaux || []).map((tb) => (
              <div key={tb.id} className="border border-white/10 p-4 mb-4" data-testid={`admin-tableau-${tb.id}`}>
                <div className="flex flex-wrap gap-3 items-center">
                  <input
                    value={tb.title || ""}
                    onChange={(e) => updateTableau(tb.id, { title: e.target.value })}
                    className={input + " flex-1 min-w-[200px]"}
                    placeholder="Titre du tableau"
                    data-testid={`admin-tableau-${tb.id}-title`}
                  />
                  <select
                    value={tb.form}
                    onChange={(e) => updateTableau(tb.id, { form: e.target.value })}
                    className={input + " bg-ink cursor-pointer sm:w-44"}
                    data-testid={`admin-tableau-${tb.id}-form`}
                  >
                    <option value="table" className="bg-ink">Tableau</option>
                    <option value="steps" className="bg-ink">Étapes</option>
                  </select>
                  <button
                    onClick={() => removeTableau(tb.id)}
                    className="text-red-400/70 hover:text-red-400 transition-colors"
                    data-testid={`admin-tableau-${tb.id}-remove`}
                    aria-label="Supprimer le tableau"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        {(tb.columns || []).map((c, ci) => (
                          <th key={ci} className="p-1 align-bottom">
                            <div className="flex items-center gap-1">
                              <input
                                value={c}
                                onChange={(e) => updateCell(tb.id, -1, ci, e.target.value)}
                                className={input + " !py-1.5 !text-[11px] font-mono"}
                                placeholder={`Colonne ${ci + 1}`}
                                data-testid={`admin-tableau-${tb.id}-col-${ci}`}
                              />
                              <button
                                onClick={() => removeCol(tb.id, ci)}
                                className="text-[#626773] hover:text-red-400 shrink-0 px-1"
                                aria-label="Retirer la colonne"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </th>
                        ))}
                        <th className="p-1 w-10">
                          <button
                            onClick={() => addCol(tb.id)}
                            className="text-gold hover:text-gold-light text-lg leading-none"
                            data-testid={`admin-tableau-${tb.id}-add-col`}
                            aria-label="Ajouter une colonne"
                          >
                            +
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tb.rows || []).map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} className="p-1">
                              <input
                                value={cell}
                                onChange={(e) => updateCell(tb.id, ri, ci, e.target.value)}
                                className={input + " !py-1.5 !text-xs"}
                                data-testid={`admin-tableau-${tb.id}-cell-${ri}-${ci}`}
                              />
                            </td>
                          ))}
                          <td className="p-1 w-10">
                            <button
                              onClick={() => removeRow(tb.id, ri)}
                              className="text-[#626773] hover:text-red-400 px-1"
                              aria-label="Retirer la ligne"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    onClick={() => addRow(tb.id)}
                    className="mt-3 text-gold hover:text-gold-light text-xs uppercase tracking-[0.15em]"
                    data-testid={`admin-tableau-${tb.id}-add-row`}
                  >
                    + Ajouter une ligne
                  </button>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={generateTableau}
                disabled={tabGenerating}
                data-testid="admin-generate-tableau"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 transition-colors disabled:opacity-50"
              >
                <Table2 className="w-4 h-4" />
                {tabGenerating ? "Génération en cours…" : "Générer avec l'IA"}
              </button>
              <button
                onClick={addManualTableau}
                data-testid="admin-add-tableau"
                className="border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-5 py-3 hover:border-gold/60 hover:text-gold transition-colors"
              >
                Créer à la main
              </button>
            </div>
            {tabError && (
              <p className="mt-2 text-xs text-red-400" data-testid="admin-tableau-error">{tabError}</p>
            )}
          </div>

          <AdminImageMap fiche={fiche} setFiche={setFiche} allFiches={allFiches} />
          </AdminSection>

          <AdminSection title="Fiche technique (champs spécifiques)" testid="admin-sec-fields">
          <div className="border border-white/10 p-6" data-testid="admin-fields-block">
            <label className="label-accent block mb-3">Champs spécifiques (fiche technique)</label>
            <p className="mb-4 text-xs text-[#626773] leading-relaxed">
              Repris de l'ancien site quand ils existaient (ADN, contexte juridique, critères…).
              Éditables librement — le contenu accepte du HTML simple (listes, liens, gras).
              Visibles côté public dans le bloc « Fiche technique ».
            </p>
            {(fiche.fields || []).length === 0 && (
              <p className="mb-4 text-xs text-[#626773] italic">Aucun champ spécifique pour cette fiche.</p>
            )}
            {(fiche.fields || []).map((fd, i) => (
              <div key={i} className="border border-white/5 p-4 mb-3" data-testid={`admin-field-${i}`}>
                <div className="flex flex-wrap gap-3 items-center">
                  <input
                    value={fd.section || ""}
                    onChange={(e) => updateField(i, { section: e.target.value })}
                    className={input + " sm:w-52"}
                    placeholder="Section (ex. Contexte juridique)"
                    data-testid={`admin-field-${i}-section`}
                  />
                  <input
                    value={fd.label || ""}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                    className={input + " flex-1 min-w-48"}
                    placeholder="Libellé (ex. Information minimale)"
                    data-testid={`admin-field-${i}-label`}
                  />
                  <button
                    onClick={() => removeField(i)}
                    className="text-red-400/70 hover:text-red-400 transition-colors"
                    data-testid={`admin-field-${i}-remove`}
                    aria-label="Supprimer le champ"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {fd.kind === "badges" ? (
                  <input
                    value={fd.html || ""}
                    onChange={(e) => updateField(i, { html: e.target.value })}
                    className={input + " mt-3"}
                    placeholder="Valeurs séparées par des virgules (pastilles)"
                    data-testid={`admin-field-${i}-html`}
                  />
                ) : (
                  <textarea
                    value={fd.html || ""}
                    onChange={(e) => updateField(i, { html: e.target.value })}
                    className={area + " mt-3 w-full min-h-[70px]"}
                    placeholder="Contenu du champ"
                    data-testid={`admin-field-${i}-html`}
                  />
                )}
              </div>
            ))}
            <button
              onClick={addField}
              data-testid="admin-add-field"
              className="border border-white/15 text-[#A0A5B1] text-xs uppercase tracking-[0.2em] px-5 py-3 hover:border-gold/60 hover:text-gold transition-colors"
            >
              + Ajouter un champ
            </button>
          </div>
          </AdminSection>

          <AdminSection title="Contenu & extrait" defaultOpen testid="admin-sec-contenu">
          <div>
            <label className="label-accent block mb-3">Extrait (cartes & référencement)</label>
            <textarea rows={3} value={fiche.excerpt} onChange={set("excerpt")} className={area} data-testid="admin-excerpt-textarea" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <label className="label-accent">Contenu</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setMode("edit")}
                  data-testid="admin-mode-edit"
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                    mode === "edit" ? "bg-gold text-ink border-gold" : "border-white/15 text-[#626773] hover:text-gold"
                  }`}
                >
                  Éditeur
                </button>
                <button
                  onClick={() => setMode("html")}
                  data-testid="admin-mode-html"
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                    mode === "html" ? "bg-gold text-ink border-gold" : "border-white/15 text-[#626773] hover:text-gold"
                  }`}
                >
                  HTML (avancé)
                </button>
              </div>
            </div>
            {mode === "html" ? (
              <textarea rows={14} value={fiche.content_html} onChange={set("content_html")} className={area} data-testid="admin-content-textarea" />
            ) : (
              <RichEditor
                key={`${fiche.slug}-${mode}`}
                initial={fiche.content_html}
                onChange={(html) => setFiche((f) => ({ ...f, content_html: html }))}
              />
            )}
            <p className="mt-3 text-xs text-[#626773]">
              Mise en forme visuelle par défaut — aucun code nécessaire. Les encarts interactifs
              (Genially, iframes…) se gèrent en mode HTML.
            </p>
          </div>
          </AdminSection>

          <AdminSection title="Assistant IA & sujets liés" testid="admin-sec-ia">
          <AiAssistant fiche={fiche} setFiche={setFiche} type={type} slug={slug} allFiches={allFiches} />

          {(fiche.related_slugs || []).length > 0 && (
            <div>
              <label className="label-accent block mb-3">
                Sujets liés {fiche.related_validated ? "(validés)" : "(proposés par IA — retirez ceux qui ne conviennent pas)"}
              </label>
              <div className="flex flex-wrap gap-2" data-testid="admin-related-chips">
                {fiche.related_slugs.map((r, idx) => (
                  <span key={idx} className="inline-flex items-center gap-2 text-xs text-gold/80 border border-gold/25 px-3 py-1.5">
                    {Array.isArray(r) ? (
                      <a
                        href={`/admin/fiches/${r[0]}/${r[1]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-gold hover:underline underline-offset-2 transition-colors"
                        data-testid={`admin-related-view-${idx}`}
                      >
                        {r[1]}
                      </a>
                    ) : (
                      r
                    )}
                    <button onClick={() => removeRelated(idx)} data-testid={`admin-related-remove-${idx}`} className="hover:text-red-400" aria-label="Retirer">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          </AdminSection>

          <div className="pt-4 flex items-center gap-4 flex-wrap">
            <button
              onClick={save}
              data-testid="admin-save-button"
              className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors"
            >
              <Save className="w-4 h-4" /> Enregistrer
            </button>
            <button
              onClick={toggleStatus}
              data-testid="admin-status-toggle-bottom"
              className={`text-xs uppercase tracking-[0.2em] px-6 py-4 border transition-colors ${
                fiche.status === "publie"
                  ? "border-red-400/40 text-red-400 hover:bg-red-400/10"
                  : "border-gold/40 text-gold hover:bg-gold/10"
              }`}
            >
              {fiche.status === "publie" ? "Dépublier" : "Publier"}
            </button>
            <a
              href={`/ressources/${type}/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-public-bottom"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#626773] hover:text-gold transition-colors"
            >
              Voir la fiche <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {saved && (
              <span className="text-gold text-sm" data-testid="admin-saved-confirmation">Modifications enregistrées.</span>
            )}
            {saveError && (
              <span className="text-red-400 text-sm" data-testid="admin-save-error">Échec de l'enregistrement — réessayez.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
